import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Typography,
  Pagination,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  CircularProgress,
} from "@mui/material";
import CollectionsIcon from "@mui/icons-material/Collections";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import { makeRequest } from "../../../../assets/functions_helper";
import {
  CustomDialog,
  CustomDialogActions,
  CustomDialogContent,
  CustomDialogTitle,
} from "../../../../components/customDialog";
import { CloseButton } from "../../../../components/buttons";
import SortableList, { SortableItem } from "react-easy-sort";
import { useNavigate } from "react-router-dom";

function BackgroundImageSelectionDialog({ open, handleClose, restaurantId, onSelectImage, onNavigateToImages }) {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all"); // "all" representa "Todas las categorías"
  const navigate = useNavigate();

  // Handler del CTA del empty state
  const handleGoToImages = () => {
    handleClose();
    if (onNavigateToImages) {
      onNavigateToImages();
    } else {
      window.dispatchEvent(
        new CustomEvent("navigateAdminTab", {
          detail: { tabName: "Imágenes" },
        })
      );
    }
  };

  useEffect(() => {
    const fetchImages = () => {
      setLoading(true);
      makeRequest(
        "GET",
        `menu_manager/list_restaurant_background_images?restaurant_id=${restaurantId}&page=${page}&size=200`,
        {},
        "application/json",
        localStorage.getItem("token"),
        localStorage.getItem("tokenType"),
        null,
        null,
        () => navigate("/login")
      )
        .then((response) => {
          setImages(response.background_images || []);
          setTotalPages(response.pages || 1);
          setLoading(false);
        })
        .catch((error) => {
          console.error("Error al obtener las imágenes:", error);
          setLoading(false);
        });
    };

    if (open) {
      fetchImages();
    }
  }, [open, restaurantId, page, navigate]);

  // Reiniciar búsqueda y filtro de categoría al abrir el diálogo
  useEffect(() => {
    if (open) {
      setSearchTerm("");
      setSelectedCategory("all");
    }
  }, [open]);

  // Reiniciar filtro de categoría al cambiar de página
  useEffect(() => {
    setSelectedCategory("all");
  }, [page]);

  // Obtener categorías únicas de las imágenes de la página actual
  const uniqueCategories = useMemo(() => {
    const categories = images
      .map((img) => img.category)
      .filter((cat) => cat && typeof cat === "string" && cat.trim() !== "");
    return [...new Set(categories)];
  }, [images]);

  const handleImageClick = (image) => {
    onSelectImage(image);
    handleClose();
  };

  const onSortEnd = () => { };

  // Filtrar imágenes por nombre y categoría
  const filteredImages = images.filter((img) => {
    const matchesName = img.image_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory =
      selectedCategory === "all" || img.category === selectedCategory;
    return matchesName && matchesCategory;
  });

  return (
    <CustomDialog open={open} onClose={handleClose}>
      <CustomDialogTitle>Selecciona una Imagen de Fondo</CustomDialogTitle>
      <CustomDialogContent>
        {/* Filtros: búsqueda y categoría */}
        <Box sx={{ mb: 2, px: 1, display: "flex", gap: 2, flexWrap: "wrap" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              background: "rgba(255,255,255,0.08)",
              boxShadow: "0px 0px 6px rgba(255,255,255,0.05) inset",
              border: "0.5px solid rgba(255,255,255,0.5)",
              borderRadius: "200px",
              px: 2,
              height: 42,
              // Conservamos las propiedades de layout del TextField original
              flex: 2,
              minWidth: "200px",
            }}
          >
            <span
              style={{
                color: "rgba(255,255,255,0.7)",
                marginRight: 8,
                fontSize: "16px",
              }}
            >
              🔍
            </span>
            <input
              type="text"
              placeholder="Buscar por nombre..." // Mantuve el placeholder original
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                background: "transparent",
                border: "none",
                outline: "none",
                color: "black", // Nota: Si tu fondo es oscuro, quizás quieras cambiar esto a "white"
                width: "100%",
                fontSize: "14px",
              }}
            />
          </Box>
          {uniqueCategories.length > 0 && (
            <FormControl size="small" sx={{ flex: 1, minWidth: "150px", backgroundColor: "#fff" }}>
              <InputLabel id="category-select-label">Categoría</InputLabel>
              <Select
                labelId="category-select-label"
                value={selectedCategory}
                label="Categoría"
                onChange={(e) => setSelectedCategory(e.target.value)}
              >
                <MenuItem value="all">Todas</MenuItem>
                {uniqueCategories.map((cat) => (
                  <MenuItem key={cat} value={cat}>
                    {cat}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>

        {loading ? (
          <Box sx={{ height: "40vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <CircularProgress size={36} sx={{ color: '#6DBBE6' }} />
          </Box>
        ) : images.length === 0 ? (
          // ── Empty State: la galería no tiene imágenes aún ──────────────────────────
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 6,
              px: 3,
              gap: 2,
              textAlign: "center",
            }}
          >
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: "50%",
                backgroundColor: "rgba(109, 187, 230, 0.12)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 1,
              }}
            >
              <CollectionsIcon sx={{ fontSize: 44, color: "#6DBBE6", opacity: 0.7 }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#1E293B" }}>
              Sin imágenes en la galería
            </Typography>
            <Typography variant="body2" sx={{ color: "#64748B", maxWidth: 320 }}>
              Aún no has creado ninguna imagen. Dirígete a la sección de imágenes para subir tus primeras fotos y podrás seleccionarlas aquí.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddPhotoAlternateIcon />}
              onClick={handleGoToImages}
              sx={{
                mt: 1,
                background: "linear-gradient(135deg, #6DBBE6 0%, #3A9BC8 100%)",
                color: "#fff",
                borderRadius: "12px",
                textTransform: "none",
                fontWeight: 700,
                px: 3,
                py: 1.2,
                boxShadow: "0 4px 14px rgba(109, 187, 230, 0.4)",
                "&:hover": {
                  background: "linear-gradient(135deg, #5AABE6 0%, #2A8BB8 100%)",
                },
              }}
            >
              Ir a crear imágenes
            </Button>
          </Box>
        ) : (
          <>
            <SortableList className="list" onSortEnd={onSortEnd}>
              {filteredImages.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 4 }}>
                  {images.length === 0
                    ? "No hay imágenes disponibles."
                    : "No se encontraron imágenes con esos filtros."}
                </Box>
              ) : (
                filteredImages.map((img, index) => (
                  <SortableItem key={img.id || index}>
                    <Box
                      className="item"
                      onClick={() => handleImageClick(img)}
                      sx={{
                        backgroundImage: `url(${img.image_url || "https://via.placeholder.com/150"})`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        width: "150px",
                        height: "150px",
                        margin: "10px",
                        display: "inline-block",
                        borderRadius: "8px",
                        overflow: "hidden",
                        cursor: "pointer",
                      }}
                    >
                      <Box
                        sx={{
                          backgroundColor: "rgba(0, 0, 0, 0.6)",
                          height: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "white",
                          p: 1,
                        }}
                      >
                        <Typography variant="body2" sx={{ textAlign: "center" }}>
                          {img.image_name || "Sin nombre"}
                        </Typography>
                      </Box>
                    </Box>
                  </SortableItem>
                ))
              )}
            </SortableList>

            {totalPages > 1 && (
              <Box display="flex" justifyContent="center" mt={2}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(_, value) => setPage(value)}
                  color="primary"
                  sx={{
                    "& .MuiPaginationItem-root": {
                      color: "#000000",
                      fontWeight: "bold",
                    },
                    "& .MuiPaginationItem-icon": {
                      color: "#000000",
                    },
                    "& .Mui-selected": {
                      backgroundColor: "rgba(0, 0, 0, 0.08) !important",
                      color: "#000000 !important",
                    },
                  }}
                />
              </Box>
            )}
          </>
        )}
      </CustomDialogContent>
      <CustomDialogActions>
        <CloseButton onClick={handleClose}>Cerrar</CloseButton>
      </CustomDialogActions>
    </CustomDialog>
  );
}

export default BackgroundImageSelectionDialog;